// Splits ONE M365 HTML message body at its embedded quote chain, keeping
// only the NEWEST content (whatever the sender actually typed in THIS
// message) for display — mirrors richTextConversion.js/placeholderCheck.js's
// convention (pure, no React, no DOM) so this is unit-testable without any
// rendering machinery. This is a DISPLAY-time parse of one message's
// existing body, not a new data model — no new DB rows, nothing persisted.
//
// WHY DROP THE QUOTED TAIL ENTIRELY (not just visually de-emphasize it):
// every outbound reply we send and every inbound message we receive is
// ALREADY captured as its own top-level `messages` row — a later message
// quoting an earlier one is always redundant with something already shown
// separately, earlier in the thread. Rendering the quoted copy too used to
// duplicate that content on screen.
//
// Detection recognizes the SAME broader set of quote conventions
// server/src/channels/m365/sanitize.js already does for its own, different,
// AI-facing reduction (QUOTE_MARKERS there) — Outlook's own structured
// divider, plus blockquote/gmail_quote/"-----Original Message-----"/
// "-----Forwarded message-----"/"On ... wrote:". Only Outlook's own divider
// (and the bare header variant below) carries structured From:/Sent:/To:/
// Subject: fields to extract; the others carry none, which is fine — this
// file only ever keeps the segment BEFORE the earliest recognized marker, so
// a dropped segment's own header fields (when it has none) are simply left
// null.
//
// Real, surveyed Outlook markup (see server/test/m365-sanitize.test.js's
// OUTLOOK_REPLY/OUTLOOK_FORWARD fixtures): Outlook wraps a quoted turn's
// envelope in `<div id="divRplyFwdMsg">`, containing bold "From:"/"Sent:"/
// "To:"/"Subject:" label/value pairs, e.g.:
//   <div id="divRplyFwdMsg"><b>From:</b> Support &lt;support@1aauto.com&gt;<br>
//   <b>Sent:</b> Monday, August 3, 2026<br><b>Subject:</b> Re: Order 12345</div>
//
// Confirmed real mail (2026-09-03) also produces this SAME bold field
// sequence with NO divRplyFwdMsg/appendonsend wrapper around it at all
// (findBareHeaderMatch below) — a bare <hr> commonly precedes it, but isn't
// required, so the bold sequence itself is the marker, independent of any
// enclosing element.
//
// Scoped to HTML M365 messages only (bodyContentType === 'html') — plain-
// text ">"-prefixed quote conventions are a different, separate parsing
// problem, deliberately not handled here.

const HEADER_DIV_RE = /<div[^>]*\bid=["']?divRplyFwdMsg["']?[^>]*>([\s\S]*?)<\/div>/gi
const HEADER_FIELD_RE = /<b>\s*(From|Sent|To|Subject)\s*:\s*<\/b>\s*([^<]*)/gi

// Every OTHER recognized quote convention, alongside divRplyFwdMsg above —
// mirrors sanitize.js's own QUOTE_MARKERS array exactly (kept as a separate,
// display-purpose copy rather than a shared import: that file reduces to
// plain text for the AI, this one preserves HTML for display, and the two
// are deliberately allowed to drift independently if either's needs change).
// appendonsend is Outlook's own "reply starts here" marker too, but carries
// no structured fields of its own — same treatment as the others here.
const OTHER_QUOTE_MARKERS = [
  /<div[^>]*id=["']?appendonsend["']?/i,
  /-{2,}\s*Original Message\s*-{2,}/i,
  /-{2,}\s*Forwarded message\s*-{2,}/i,
  /<blockquote/i,
  // Anchored at the tag's own "<", as in sanitize.js: matching from class=
  // cut inside the tag and left a dangling "<div" on the kept segment.
  /<[^>]*class=["'][^"']*gmail_quote/i,
  /On\s[^<>]{5,120}?\swrote:/i,
]

function decodeEntities(s) {
  return s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .trim()
}

// Extracts From/Sent/To/Subject from a divRplyFwdMsg block's inner HTML, and
// validates it's a REAL header block (From, Sent, and Subject all present,
// in that relative order — To, when present, must sit between Sent and
// Subject) rather than a coincidental match on unrelated bold text. Returns
// null for anything that doesn't confidently validate — the caller must
// treat that occurrence as "not a recognized marker" and leave it unsplit,
// per this file's defensive design (an undersplit chain is a much smaller
// problem than a wrongly-split one).
function parseHeaderFields(innerHtml) {
  const fields = {}
  const positions = {}
  const endPositions = {}
  HEADER_FIELD_RE.lastIndex = 0
  let m
  while ((m = HEADER_FIELD_RE.exec(innerHtml))) {
    const label = m[1].toLowerCase()
    if (!(label in fields)) {
      fields[label] = decodeEntities(m[2])
      positions[label] = m.index
      endPositions[label] = m.index + m[0].length
    }
  }
  if (!('from' in fields) || !('sent' in fields) || !('subject' in fields)) return null
  if (!(positions.from < positions.sent && positions.sent < positions.subject)) return null
  if ('to' in fields && !(positions.sent < positions.to && positions.to < positions.subject)) return null
  return {
    headerFrom: fields.from,
    headerSentRaw: fields.sent,
    headerTo: fields.to ?? null,
    headerSubject: fields.subject,
    // Offset (within innerHtml) of the end of the Subject field's match —
    // used by the bare-header path below, which has no enclosing div to
    // anchor its own "end of header" cut point to.
    headerSpanEnd: endPositions.subject,
  }
}

// Outlook's plain reply-quote header can appear with NO divRplyFwdMsg/
// appendonsend wrapper at all — real mail confirmed a bare From:/Sent:/To:/
// Subject: bold-label sequence with nothing wrapping it (a bare <hr>
// commonly precedes it, matching sanitize.js's own server-side note, but
// isn't required — some clients omit it). Search for the bold "From:" label
// directly and validate the same way as the wrapped case, using a bounded
// window so a coincidental "From:" much earlier in the body can't run away
// into unrelated later content.
const BARE_FROM_LABEL_RE = /<b>\s*From\s*:\s*<\/b>/i
const BARE_HEADER_WINDOW = 800

function findBareHeaderMatch(body) {
  const fm = BARE_FROM_LABEL_RE.exec(body)
  if (!fm) return null
  const windowHtml = body.slice(fm.index, fm.index + BARE_HEADER_WINDOW)
  const fields = parseHeaderFields(windowHtml)
  if (!fields) return null
  return { ...fields, matchStart: fm.index, matchEnd: fm.index + fields.headerSpanEnd }
}

// html -> an array of AT MOST TWO segments, each { headerFrom, headerSentRaw,
// headerTo, headerSubject, bodyHtml }: [0] is always the newest content —
// whatever precedes the EARLIEST recognized quote marker (headerFrom always
// null; for a message with no quote chain at all, this is the WHOLE body,
// returned as the single segment — a no-op) — and, when a marker was found,
// [1] is everything from that marker onward, undifferentiated (never
// further split into per-turn segments: nothing renders it, since the
// caller only ever keeps [0] — see this file's own header comment on why).
// [1]'s header fields are populated only when the marker that triggered the
// cut was Outlook's own divRplyFwdMsg AND its fields validated; every other
// marker kind carries none, so they stay null.
export function splitEmailChain(html) {
  const body = html ?? ''

  // Outlook's own structured divider — the ONLY marker with fields to
  // extract, and the only one that needs validating (see parseHeaderFields's
  // own comment on why a coincidental bold-text match must not count).
  let divMatch = null
  HEADER_DIV_RE.lastIndex = 0
  let m
  while ((m = HEADER_DIV_RE.exec(body))) {
    const fields = parseHeaderFields(m[1])
    if (fields) { divMatch = { ...fields, matchStart: m.index, matchEnd: m.index + m[0].length }; break }
  }

  // The same bold From:/Sent:/To:/Subject: sequence, but with no wrapping
  // div at all — a separate candidate from divMatch above (see
  // findBareHeaderMatch's own comment on why).
  const bareMatch = findBareHeaderMatch(body)

  // Earliest occurrence of any of the other recognized conventions.
  let otherIndex = -1
  for (const re of OTHER_QUOTE_MARKERS) {
    const om = re.exec(body)
    if (om && (otherIndex === -1 || om.index < otherIndex)) otherIndex = om.index
  }

  if (!divMatch && !bareMatch && otherIndex === -1) {
    return [{ headerFrom: null, headerSentRaw: null, headerTo: null, headerSubject: null, bodyHtml: body }]
  }

  // Whichever recognized marker occurs FIRST in the body wins the cut point.
  // On an exact tie (only possible between div and bare, since bare's own
  // "From:" label always sits AT OR AFTER a div wrapping it), div wins —
  // it's the more specific match and its matchEnd correctly excludes the
  // whole envelope div, not just the header text within it.
  const candidates = [
    divMatch && { kind: 'div', ...divMatch },
    bareMatch && { kind: 'bare', ...bareMatch },
    otherIndex !== -1 && { kind: 'other', matchStart: otherIndex, matchEnd: otherIndex, headerFrom: null, headerSentRaw: null, headerTo: null, headerSubject: null },
  ].filter(Boolean)
  const rank = { div: 0, bare: 1, other: 2 }
  candidates.sort((a, b) => (a.matchStart - b.matchStart) || (rank[a.kind] - rank[b.kind]))
  const winner = candidates[0]

  return [
    { headerFrom: null, headerSentRaw: null, headerTo: null, headerSubject: null, bodyHtml: body.slice(0, winner.matchStart) },
    {
      headerFrom: winner.headerFrom,
      headerSentRaw: winner.headerSentRaw,
      headerTo: winner.headerTo,
      headerSubject: winner.headerSubject,
      bodyHtml: body.slice(winner.matchEnd),
    },
  ]
}

// Whether an HTML fragment shows anything (2026-10-13, prod contact
// 7143870): a Gmail reply can put its whole body INSIDE the gmail_quote
// wrapper, so segment[0] of splitEmailChain() is only an empty <div> and the
// bubble rendered blank. Whitespace, &nbsp; (and other space entities),
// zero-width characters, <br> and empty elements don't count; any text does,
// and so does an image or other embedded media. Comments, <style>, <script>
// and <head> never count.
const MEDIA_TAG_RE = /<(img|video|audio|svg|picture|object|embed|iframe|canvas)\b/i
export function hasVisibleContent(html) {
  const s = String(html ?? '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(style|script|head|title)[^>]*>[\s\S]*?<\/\1>/gi, '')
  if (MEDIA_TAG_RE.test(s)) return true
  const text = s
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(nbsp|ensp|emsp|thinsp|zwnj|zwj|shy);/gi, ' ')
    .replace(/&#(160|8203|8204|8205|8288|65279|173|8194|8195|8201);/g, ' ')
    .replace(/&#x(a0|200b|200c|200d|2060|feff|ad);/gi, ' ')
    .replace(/[ ​-‍⁠﻿­]/g, ' ')
  return text.trim().length > 0
}

// The HTML a message bubble shows: the newest content (segment[0]) when it
// shows anything, else the whole body, as received.
export function newestContentOrWholeBody(html) {
  const body = html ?? ''
  const newest = splitEmailChain(body)[0].bodyHtml
  return hasVisibleContent(newest) ? newest : body
}
